import { Box, Input, Typography } from '@mui/material';
import { color } from '@/colors';

interface DashboardInlineEditInputProps {
  isEditing: boolean;
  value: string;
  displayValue: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  'data-testid'?: string;
}

const contentStyles = {
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.2,
} as const;

export function DashboardInlineEditInput({
  isEditing,
  value,
  displayValue,
  onChange,
  onKeyDown,
  onBlur,
  'data-testid': dataTestId,
}: DashboardInlineEditInputProps) {
  return (
    <Box sx={{ py: 0.5, position: 'relative', minHeight: '1.5em' }} data-testid={dataTestId}>
      {/* Typography always in flow to define height; hidden when editing */}
      <Typography
        component="div"
        variant="body-std-rg"
        sx={{
          ...contentStyles,
          display: 'block',
          visibility: isEditing ? 'hidden' : 'visible',
        }}
      >
        {isEditing ? value : displayValue}
      </Typography>
      {isEditing && (
        <Input
          fullWidth
          multiline
          inputRef={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
            if (el) el.focus();
          }}
          onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            p: 0,
            ...contentStyles,
            color: 'inherit',
            '&::before': { borderBottomColor: color.White64_T },
            '&::after': { borderBottomColor: color.White64_T },
            '& input, & textarea': {
              ...contentStyles,
              color: 'inherit',
              '&::selection': { backgroundColor: color.TextSelection },
            },
            '& textarea': { resize: 'none' },
          }}
        />
      )}
    </Box>
  );
}
